import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { showToast } from '@/config/toast.config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FloppyDisk as FloppyDiskIcon, Camera as CameraIcon, X as XIcon, Check as CheckIcon } from '@phosphor-icons/react';
import TextArea from '@/components/common/TextArea/TextArea';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Alert from '@/components/common/Alert/Alert';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

export default function PublicQuoteActionsPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const vehicleFileInputsRef = useRef({});
  const lineItemFileInputsRef = useRef({});
  const [commentDrafts, setCommentDrafts] = useState({}); // { [lineItemId]: string }
  // Expanded rows removed; we now use a dialog per line item
  const [lineItemFileCounts, setLineItemFileCounts] = useState({}); // { [lineItemId]: number }
  const [vehicleFileCounts, setVehicleFileCounts] = useState({}); // { [vehicleId]: number }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const vehicleSelectedFilesRef = useRef({}); // { [vehicleId]: File[] }
  const lineItemSelectedFilesRef = useRef({}); // { [lineItemId]: File[] }
  // Gallery state removed for stripped down version

  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif'];
  const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/bmp', 'image/heic', 'image/heif'];
  const ALLOWED_FORMATS_LABEL = '.jpg, .jpeg, .png, .webp, .gif, .bmp, .heic, .heif';

  // Error reporting helpers to enrich Discord notifications from the backend
  const serializeErrorBasic = (err) => {
    try {
      if (!err) return null;
      const base = {
        name: err?.name || null,
        message: err?.message || null,
        stack: err?.stack || null,
        type: typeof err,
        constructorName: err && err.constructor ? err.constructor.name : null,
      };
      const cause = (() => {
        try { return err?.cause; } catch { return null; }
      })();
      if (cause) {
        base.cause = {
          name: cause?.name || null,
          message: cause?.message || null,
          stack: cause?.stack || null,
          constructorName: cause && cause.constructor ? cause.constructor.name : null,
        };
      }
      return base;
    } catch {
      return { value: String(err) };
    }
  };

  const sendClientError = (message, error, meta) => {
    try {
      const payload = {
        message,
        error: String(error?.message || error || ''),
        stack: String(error?.stack || ''),
        url: typeof location !== 'undefined' ? location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        meta: {
          error_details: serializeErrorBasic(error),
          ...meta,
        },
      };
      navigator.sendBeacon?.(
        `${API_BASE}/public/quotes/${encodeURIComponent(token)}/client-error`,
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
    } catch { /* ignore */ }
  };

  const isNetworkFetchError = (err) => {
    try {
      const msg = String(err?.message || '').toLowerCase();
      // Chrome/Android: "Failed to fetch"
      // iOS Safari: "Load failed"
      // Some browsers: "NetworkError when attempting to fetch resource."
      return err?.name === 'TypeError' && (
        msg.includes('failed to fetch') ||
        msg.includes('load failed') ||
        msg.includes('networkerror') ||
        msg.includes('network')
      );
    } catch {
      return false;
    }
  };

  const getSelectedFilesDiagnostics = () => {
    try {
      const vehicleEntries = Object.entries(vehicleSelectedFilesRef.current || {});
      const lineItemEntries = Object.entries(lineItemSelectedFilesRef.current || {});

      const vehicleFiles = vehicleEntries.flatMap(([vehicleId, files]) =>
        (files || []).map((f) => ({ scope: 'vehicle', id: String(vehicleId), name: String(f?.name || ''), size: Number(f?.size || 0), type: String(f?.type || '') }))
      );
      const lineItemFiles = lineItemEntries.flatMap(([lineItemId, files]) =>
        (files || []).map((f) => ({ scope: 'line_item', id: String(lineItemId), name: String(f?.name || ''), size: Number(f?.size || 0), type: String(f?.type || '') }))
      );

      const all = [...vehicleFiles, ...lineItemFiles];
      const totalBytes = all.reduce((a, f) => a + (Number(f.size) || 0), 0);

      // Keep payload small: include only top 30 largest files
      const largest = all
        .slice()
        .sort((a, b) => (Number(b.size) || 0) - (Number(a.size) || 0))
        .slice(0, 30);

      return {
        total_files: all.length,
        total_bytes: totalBytes,
        largest_files: largest,
      };
    } catch {
      return null;
    }
  };

  const submitWithRetry = async (submitUrl, formData, { retries = 1, retryDelayMs = 600 } = {}) => {
    let lastErr = null;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        // Avoid caching layers doing anything surprising
        const res = await fetch(submitUrl, { method: 'POST', body: formData, cache: 'no-store' });
        return res;
      } catch (err) {
        lastErr = err;
        if (!isNetworkFetchError(err) || attempt === retries) break;
        // Small delay before retry; helps with brief mobile drops / captive portal transitions
        await new Promise((r) => setTimeout(r, retryDelayMs));
      }
    }
    throw lastErr;
  };

  const isImageFile = (file) => {
    try {
      if (file && typeof file.type === 'string' && ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) return true;
      const name = String(file?.name || file?.filename || '');
      const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
      return ALLOWED_EXTENSIONS.includes(ext);
    } catch {
      return false;
    }
  };

  // Simple Romanian pluralization for image selection counter
  const pluralizeImagesRo = (count) => {
    if (!count || Number(count) === 0) return 'Adaugă imagini piesă';
    return Number(count) === 1 ? '1 imagine adaugată' : `${count} imagini adaugate`;
  };
  const pluralizeVehicleImagesRo = (count) => {
    if (!count || Number(count) === 0) return 'Adaugă imagini talon + vehicul';
    return Number(count) === 1 ? '1 imagine vehicul' : `${count} imagini vehicul`;
  };

  // Date helpers removed for stripped down version

  // valueBadge removed: we'll inline classes where still needed

  // Labels for upload buttons
  const getVehicleUploadLabel = (vehicleId) => pluralizeVehicleImagesRo(vehicleFileCounts[String(vehicleId)] || 0);
  const getLineItemUploadLabel = (lineItemId) => pluralizeImagesRo(lineItemFileCounts[String(lineItemId)] || 0);

  const isVehicleSaleLineItem = (lineItem) => String(lineItem?.line_item_type || '') === 'vehicle_sale';

  const getVehicleDisplayName = (vehicle) => {
    const parts = vehicle ? [
      vehicle.manufacturer_name || null,
      vehicle.model_name || null,
      vehicle.build_year || null,
      vehicle.engine_name || null,
      (vehicle.engine_code_name && String(vehicle.engine_code_name).trim().length > 0) ? vehicle.engine_code_name : null,
      (vehicle.engine_type && String(vehicle.engine_type).trim().length > 0) ? vehicle.engine_type : null
    ].filter(Boolean) : [];
    return parts.length ? parts.join(' ') : 'Vehicul nespecificat';
  };

  const getVehicleCardTitle = (vehicle, isVehicleSaleGroup) => {
    const vehicleName = getVehicleDisplayName(vehicle);
    if (!isVehicleSaleGroup) return vehicleName;
    if (!vehicle || vehicleName === 'Vehicul nespecificat') return 'Vehicul pentru dezmembrare';
    return `Vehicul pentru dezmembrare ${vehicleName}`;
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        if (!API_BASE) {
          throw new Error('VITE_API_BASE_URL nu este setată');
        }
        // Status-based redirects are handled in entry router; proceed to load data
        // Light runtime hint for preview when crawlers don't execute full app
        try {
          document.title = 'dezShop - Zona client';
          const metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            const m = document.createElement('meta');
            m.name = 'description';
            m.content = 'Accesează linkurile primite: oferte, încărcare imagini, retururi și altele.';
            document.head.appendChild(m);
          }
        } catch (error) {
          showToast.error(error, { disableCopy: true });
          // Best-effort report to backend for client-side errors
          try {
            navigator.sendBeacon?.(
              `${API_BASE}/public/quotes/${encodeURIComponent(token)}/client-error`,
              new Blob([
                JSON.stringify({
                  message: 'Failed to set document metadata',
                  error: String(error?.message || error),
                  stack: String(error?.stack || ''),
                  url: typeof location !== 'undefined' ? location.href : '',
                  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                })
              ], { type: 'application/json' })
            );
          } catch { /* ignore */ }
        }
        const noCountParam = (() => {
          const v = String(searchParams.get('no_count') || '').toLowerCase();
          return v === '1' || v === 'true' || v === 'yes';
        })();
        const res = await fetch(`${API_BASE}/public/quotes/${encodeURIComponent(token)}${noCountParam ? '?no_count=1' : ''}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const err = new Error(body?.detail || `HTTP ${res.status}`);
          err.status = res.status;
          // Route within app: expired links go to expired, others to info
          if (res.status === 404 || res.status === 410) {
            navigate('/expired');
          } else {
            navigate('/info');
          }
          try {
            navigator.sendBeacon?.(
              `${API_BASE}/public/quotes/${encodeURIComponent(token)}/client-error`,
              new Blob([
                JSON.stringify({
                  message: 'GET public quote failed',
                  error: String(err?.message || err),
                  status: res.status,
                  url: `${API_BASE}/public/quotes/${encodeURIComponent(token)}`,
                })
              ], { type: 'application/json' })
            );
          } catch { /* ignore */ }
          try {
            navigator.sendBeacon?.(
              `${API_BASE}/public/quotes/${encodeURIComponent(token)}/client-error`,
              new Blob([
                JSON.stringify({
                  message: 'Redirecting to homepage due to fetch failure',
                })
              ], { type: 'application/json' })
            );
          } catch { /* ignore */ }
          throw err;
        }
        const json = await res.json();
        if (isMounted) setData(json);
      } catch (error) {
        showToast.error(error, { disableCopy: true });
        // Report catch errors as well
        try {
          navigator.sendBeacon?.(
            `${API_BASE}/public/quotes/${encodeURIComponent(token)}/client-error`,
            new Blob([
              JSON.stringify({
                message: 'Unhandled error loading public quote',
                error: String(error?.message || error),
                stack: String(error?.stack || ''),
                url: typeof location !== 'undefined' ? location.href : '',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
              })
            ], { type: 'application/json' })
          );
        } catch { /* ignore */ }
        // Fail-safe: keep users within app
        try { navigate('/info'); } catch { /* ignore */ }
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [token, searchParams, navigate]);

  const triggerVehicleUpload = (vehicleId) => {
    try {
      const el = vehicleFileInputsRef.current[String(vehicleId)];
      if (!el) return;
      // Reset value before opening to allow same-file reselection on Android
      el.value = '';
      el.click();
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    }
  };

  const handleVehicleFilesSelected = (vehicleId, e) => {
    try {
      const files = Array.from(e.target.files || []);
      const invalid = files.filter(f => !isImageFile(f));
      const valid = files.filter(isImageFile);
      if (invalid.length > 0) {
        const preview = invalid.map(f => f?.name || 'fără nume').slice(0, 3).join(', ');
        const suffix = invalid.length > 3 ? ` (+${invalid.length - 3})` : '';
        showToast.error(new Error(`Format neacceptat. Formate permise: ${ALLOWED_FORMATS_LABEL}. Fișiere ignorate: ${preview}${suffix}`), { disableCopy: true });
      }
      setVehicleFileCounts(prev => ({ ...prev, [String(vehicleId)]: valid.length }));
      vehicleSelectedFilesRef.current[String(vehicleId)] = valid;
      if (valid.length > 0) {
        showToast.info(`Selectate ${valid.length} imagini pentru vehicul.`);
      }
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    } finally {
      // Reset input so selecting same files again will retrigger change
      if (e?.target) e.target.value = '';
    }
  };

  const triggerLineItemUpload = (lineItemId) => {
    try {
      const el = lineItemFileInputsRef.current[String(lineItemId)];
      if (!el) return;
      // Reset value before opening to allow same-file reselection on Android
      el.value = '';
      el.click();
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    }
  };

  const handleLineItemFilesSelected = (lineItemId, e) => {
    try {
      const files = Array.from(e.target.files || []);
      const invalid = files.filter(f => !isImageFile(f));
      const valid = files.filter(isImageFile);
      if (invalid.length > 0) {
        const preview = invalid.map(f => f?.name || 'fără nume').slice(0, 3).join(', ');
        const suffix = invalid.length > 3 ? ` (+${invalid.length - 3})` : '';
        showToast.error(new Error(`Format neacceptat. Formate permise: ${ALLOWED_FORMATS_LABEL}. Fișiere ignorate: ${preview}${suffix}`), { disableCopy: true });
      }
      setLineItemFileCounts(prev => ({ ...prev, [String(lineItemId)]: valid.length }));
      lineItemSelectedFilesRef.current[String(lineItemId)] = valid;
      if (valid.length > 0) { showToast.info(`Selectate ${valid.length} imagini pentru articol.`); }
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    } finally {
      if (e?.target) e.target.value = '';
    }
  };

  // handleDraftChange removed; dialog edits handled via setLineItemDialogComment

  

  // Line item dialog state and handlers
  const [lineItemDialogOpen, setLineItemDialogOpen] = useState(false);
  const [activeLineItemId, setActiveLineItemId] = useState(null);
  const [lineItemDialogComment, setLineItemDialogComment] = useState('');
  const lineItemTextAreaRef = useRef(null);

  useEffect(() => {
    if (lineItemDialogOpen) {
      setTimeout(() => {
        try {
          lineItemTextAreaRef.current?.focus?.();
        } catch (error) {
          showToast.error(error, { disableCopy: true });
        }
      }, 0);
    }
  }, [lineItemDialogOpen, activeLineItemId]);

  const openLineItemDialog = (lineItemId) => {
    try {
      const key = String(lineItemId);
      setActiveLineItemId(key);
      setLineItemDialogComment(String(commentDrafts[key] || ''));
      setLineItemDialogOpen(true);
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    }
  };

  const handleLineItemDialogSave = () => {
    try {
      if (!activeLineItemId) {
        setLineItemDialogOpen(false);
        return;
      }
      setCommentDrafts(prev => ({ ...prev, [String(activeLineItemId)]: lineItemDialogComment }));
      setLineItemDialogOpen(false);
      setActiveLineItemId(null);
      setLineItemDialogComment('');
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    }
  };

  const handleLineItemDialogCancel = () => {
    try {
      setLineItemDialogOpen(false);
      setActiveLineItemId(null);
      setLineItemDialogComment('');
    } catch (error) {
      showToast.error(error, { disableCopy: true });
    }
  };

  // Track a simple change counter: selected images + non-empty comments
  const changesCount = (() => {
    const imageSelections = Object.values(lineItemFileCounts).reduce((a, b) => a + (Number(b) || 0), 0);
    const vehicleSelections = Object.values(vehicleFileCounts).reduce((a, b) => a + (Number(b) || 0), 0);
    const comments = Object.values(commentDrafts).filter(v => (v || '').trim().length > 0).length;
    return imageSelections + vehicleSelections + comments;
  })();

  const handleSaveClick = () => {
    if (changesCount === 0) {
      showToast.info('Nu există modificări de salvat.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (changesCount === 0) {
      showToast.info('Nu ai adăugat informații. Adaugă note sau imagini înainte de a trimite.');
      setConfirmOpen(false);
      return;
    }
    try {
      const form = new FormData();
      const vehicles = [];
      const lineItems = [];

      const knownVehicleIds = new Set((data.vehicles || []).map(v => String(v?.id)));

      // Build ordered vehicles payload (for known vehicles) and append files
      (data.vehicles || []).forEach(v => {
        const vid = String(v.id);
        const files = vehicleSelectedFilesRef.current[vid] || [];
        vehicles.push({ id: vid, comment: null, image_count: files.length });
        files.forEach(f => form.append('files', f));
      });

      // Add "orphan" vehicle uploads (e.g. when the quote has no vehicle record but UI still offers talon/vehicle images)
      try {
        Object.entries(vehicleSelectedFilesRef.current || {}).forEach(([rawId, files]) => {
          const id = String(rawId);
          const selected = files || [];
          if (knownVehicleIds.has(id)) return;
          if (selected.length <= 0) return;
          vehicles.push({ id, comment: null, image_count: selected.length });
          selected.forEach(f => form.append('files', f));
        });
      } catch { /* ignore */ }

      // Build ordered line items and append files
      (data.line_items || []).forEach(li => {
        const lid = String(li.id);
        const files = lineItemSelectedFilesRef.current[lid] || [];
        const comment = (commentDrafts[lid] || '').trim() || null;
        lineItems.push({ id: lid, comment, image_count: files.length });
        files.forEach(f => form.append('files', f));
      });

      form.append('payload', JSON.stringify({ vehicles, line_items: lineItems }));

      setSubmitting(true);
      const submitUrl = `${API_BASE}/public/quotes/${encodeURIComponent(token)}/submit`;
      const fileDiag = getSelectedFilesDiagnostics();
      const res = await submitWithRetry(submitUrl, form, { retries: 1, retryDelayMs: 650 });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body?.detail || `HTTP ${res.status}`);
        err.status = res.status;
        // If the link expired or became invalid while the user was on the page, handle gracefully
        if (res.status === 404 || res.status === 410) {
          try {
            const statusRes = await fetch(`${API_BASE}/public/quotes/${encodeURIComponent(token)}/status`);
            if (statusRes.ok) {
              const statusJson = await statusRes.json();
              const st = String(statusJson?.status || '').toLowerCase();
              setConfirmOpen(false);
              if (st === 'expired') {
                showToast.error(new Error('Linkul a expirat. Te rugăm să soliciți un link nou.'), { disableCopy: true });
                navigate('/expired');
                return;
              }
              if (st === 'invalid' || st === 'revoked') {
                showToast.error(new Error('Linkul a expirat sau a fost revocat. Te rugăm să soliciți un link nou.'), { disableCopy: true });
                navigate('/expired');
                return;
              }
            }
          } catch (statusError) {
            // Fall through to generic handling; also report
            showToast.error(statusError, { disableCopy: true });
          }
        }
        try {
          navigator.sendBeacon?.(
            `${API_BASE}/public/quotes/${encodeURIComponent(token)}/client-error`,
            new Blob([
              JSON.stringify({
                message: 'Submit public quote failed',
                error: String(err?.message || err),
                stack: String(err?.stack || ''),
                url: typeof location !== 'undefined' ? location.href : '',
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                counts: {
                  vehicles: vehicles.length,
                  lineItems: lineItems.length,
                },
                upload: fileDiag,
              })
            ], { type: 'application/json' })
          );
        } catch { /* ignore */ }
        // Mark as already reported so we don't double-report in the catch below
        try { err._clientErrorReported = true; } catch { /* ignore */ }
        throw err;
      }
      // Clear local state and collapse UI
      vehicleSelectedFilesRef.current = {};
      lineItemSelectedFilesRef.current = {};
      setLineItemFileCounts({});
      setVehicleFileCounts({});
      setCommentDrafts({});
      setConfirmOpen(false);
      showToast.success('Mulțumim! Trimiterea a fost înregistrată.');
      navigate('/thanks');
    } catch (error) {
      // Network errors (e.g. "Failed to fetch") can mean:
      // - client connectivity dropped mid-upload
      // - proxy/body-size limit closed the connection
      // - server processed request but response didn't reach client ("phantom success")
      const isNetworkError = isNetworkFetchError(error);
      
      if (isNetworkError) {
        try {
          const statusRes = await fetch(`${API_BASE}/public/quotes/${encodeURIComponent(token)}/status`);
          if (statusRes.ok) {
            const statusJson = await statusRes.json();
            const st = String(statusJson?.status || '').toLowerCase();
            if (st === 'submitted') {
              // The submission actually succeeded! The response just didn't make it back
              setConfirmOpen(false);
              vehicleSelectedFilesRef.current = {};
              lineItemSelectedFilesRef.current = {};
              setLineItemFileCounts({});
              setVehicleFileCounts({});
              setCommentDrafts({});
              showToast.success('Mulțumim! Trimiterea a fost înregistrată.');
              navigate('/thanks');
              return;
            }
          }
        } catch {
          // Status check failed, continue with normal error handling
        }
      }

      // Give a more actionable message for network errors, since "Failed to fetch" is vague
      if (isNetworkError) {
        showToast.error(new Error('Conexiunea s-a întrerupt în timpul trimiterii. Încearcă din nou (ideal pe Wi‑Fi) sau trimite mai puține imagini odată.'), { disableCopy: true });
      } else {
      showToast.error(error, { disableCopy: true });
      }
      // Enrich Discord message with selection context so issues are easier to diagnose
      const vehicleSelections = (() => {
        try {
          return Object.entries(vehicleSelectedFilesRef.current || {}).map(([id, files]) => ({ id: String(id), images: (files || []).length })).slice(0, 50);
        } catch { return []; }
      })();
      const lineItemSelections = (() => {
        try {
          return Object.entries(lineItemSelectedFilesRef.current || {}).map(([id, files]) => ({ id: String(id), images: (files || []).length })).slice(0, 100);
        } catch { return []; }
      })();
      const nonEmptyComments = (() => {
        try { return Object.values(commentDrafts || {}).filter(v => (v || '').trim().length > 0).length; } catch { return 0; }
      })();
      // Avoid double-reporting HTTP failures (we already report in the !res.ok path above)
      const alreadyReported = !!(error && error._clientErrorReported);
      if (!alreadyReported) {
      sendClientError('Unhandled submit error in client', error, {
        counts: {
          vehicles_total: (data?.vehicles || []).length,
          line_items_total: (data?.line_items || []).length,
          vehicle_files_selected_total: Object.values(vehicleFileCounts || {}).reduce((a, b) => a + (Number(b) || 0), 0),
          line_item_files_selected_total: Object.values(lineItemFileCounts || {}).reduce((a, b) => a + (Number(b) || 0), 0),
          comments_non_empty: nonEmptyComments,
          changesCount,
        },
          upload: getSelectedFilesDiagnostics(),
        selections: {
          vehicle_files: vehicleSelections,
          line_item_files: lineItemSelections,
        },
        activeLineItemId: activeLineItemId ? String(activeLineItemId) : null,
        phantom_success_check: isNetworkError ? 'checked_not_submitted' : 'not_network_error',
          client_submit_retry: isNetworkError ? 'enabled_1_retry' : 'not_applicable',
      });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Image mapping and gallery opener removed for stripped down version

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If data failed to load, we already redirected away; render nothing
  if (!data) return null;

  return (
    <div className={`w-full mx-auto px-3 sm:px-6 py-4 font-sans max-w-[1100px] ${changesCount > 0 ? 'pb-24 sm:pb-4' : ''}`}>
      {/* Unsaved changes warning banner */}
      {changesCount > 0 && (
        <div className="mb-4 animate-in slide-in-from-top-2 duration-300">
          <Alert type="warning" compact>
            <Alert.Header className="font-semibold text-base">
              Ai modificări nesalvate ({changesCount})
            </Alert.Header>
            <Alert.Text className="text-sm mt-1">
              Nu uita să salvezi și să trimeți informațiile înainte de a părăsi pagina.
            </Alert.Text>
          </Alert>
        </div>
      )}

      {/* Mobile fixed bottom action bar */}
      {changesCount > 0 && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-white/90 backdrop-blur px-4 py-3">
          <Button
            variant="primary"
            size="md"
            onClick={handleSaveClick}
            className="w-full h-14 text-base"
          >
            <FloppyDiskIcon size={24} weight="regular" className="mr-1.5" />
            {`Salvează și trimite informațiile (${changesCount})`}
          </Button>
        </div>
      )}

      {/* Desktop floating save button bottom-right */}
      {changesCount > 0 && (
        <div className="hidden sm:flex fixed bottom-6 right-6 z-40">
          <Button
            variant="primary"
            size="md"
            onClick={handleSaveClick}
            className="h-14 text-base shadow-lg"
          >
            <FloppyDiskIcon size={24} weight="regular" className="mr-1.5" />
            {`Salvează și trimite informațiile (${changesCount})`}
          </Button>
        </div>
      )}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent hideClose preventClose={submitting || true}>
          <DialogHeader>
            <DialogTitle className="text-2xl sm:text-3xl">Confirmă trimiterea</DialogTitle>
          </DialogHeader>
          <div className="mt-2 grid grid-cols-2 gap-2 w-full">
            <Button variant="outline" size="md" className="text-base w-full" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              <XIcon size={18} weight="regular" className="mr-1.5" />
              Anulează
            </Button>
            <Button
              variant="primary"
              size="md"
              className="text-base w-full"
              onClick={handleConfirmSubmit}
              isLoading={submitting}
              disabled={submitting}
              iconLeft={<CheckIcon size={18} weight="regular" />}
            >
              Confirmă
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      

      {/* Quote details card - commented out for stripped down version
      <section className="mb-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-base sm:text-[17px]">
              <div className="flex items-center gap-2">
                <QuotesIcon size={20} weight="duotone" className="text-brand-primary" />
                <span>Cerere</span>
              </div>
              <span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{data.id}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm space-y-1.5">
              {(() => {
                const createdAt = data.created_at || data.createdAt || data.created_date || data.createdDate || data.created;
                const formatted = formatDateTimeRo(createdAt);
                return formatted ? (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Creată la:</span>
                    <span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{formatted}</span>
                  </div>
                ) : null;
              })()}
              {data.link_expires_at && (() => {
                const d = parseUtc(data.link_expires_at);
                if (!d || Number.isNaN(d.getTime())) return null;
                const now = new Date();
                const ms = d.getTime() - now.getTime();
                const remaining = ms <= 0 ? 'expirat' : (() => {
                  const mins = Math.floor(ms / 60000);
                  if (mins < 60) return `${mins} min`;
                  const hrs = Math.floor(mins / 60);
                  if (hrs < 24) return `${hrs} ore`;
                  const days = Math.floor(hrs / 24);
                  return `${days} zile`;
                })();
                return (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Link expiră în:</span>
                    <span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{remaining}</span>
                  </div>
                );
              })()}
              {data.contact_name_masked && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Contact:</span>
                  <span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{data.contact_name_masked}</span>
                </div>
              )}
              {data.contact_phone_masked && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Telefon:</span>
                  <span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{data.contact_phone_masked}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </section>
      */}

      {/* Page title */}
      <div className="mb-3 sm:mb-4">
        <h4 className="text-gray-400 text-center">
          Adaugă informații pentru cererea ta
        </h4>
      </div>

      {/* Friendly helper text - commented out for stripped down version
      <section className="mb-4">
        <Alert type="info" compact>
          <Alert.Header>Ajută-ne să răspundem rapid</Alert.Header>
          <Alert.Details>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Piese: fotografii clare și coduri OEM ne ajută cel mai mult.</li>
              <li>Vehicul: ideal o poză cu talonul și, dacă se poate, câteva fotografii ale mașinii.</li>
            </ul>
          </Alert.Details>
        </Alert>
      </section>
      */}

      {/* Group by vehicle: each vehicle card contains its parts */}
      {(() => {
        const vehicleById = Object.fromEntries((data.vehicles || []).map(v => [String(v.id), v]));
        const grouped = new Map();
        (data.line_items || []).forEach(li => {
          const vid = String(li.vehicle?.id || 'none');
          if (!grouped.has(vid)) grouped.set(vid, []);
          grouped.get(vid).push(li);
        });

        const order = (data.vehicles || []).map(v => String(v.id));
        const keys = Array.from(grouped.keys()).sort((a, b) => order.indexOf(a) - order.indexOf(b));

        return (
          <section className="space-y-8">
            {keys.map((vid) => {
              const v = vehicleById[vid];
              const items = grouped.get(vid) || [];
              const isVehicleSaleGroup = items.length > 0 && items.every(isVehicleSaleLineItem);
              return (
                <Card key={vid}>
                  <CardHeader>
                <CardTitle className="text-base sm:text-[16px] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {/* <VehiclesIcon size={24} weight="duotone" className="text-brand-primary" /> */}
                    <span className="whitespace-normal break-words sm:truncate leading-snug text-lg sm:text-xl">
                      {getVehicleCardTitle(v, isVehicleSaleGroup)}
                    </span>
                    {/* Vehicle images disabled for now
                    {v?.images?.length ? (
                      <span className="inline-block ml-1">
                        <LineItemImageThumb
                          images={v.images}
                          type="customer"
                          disableZoom
                          size="md"
                          onClick={() => openGallery(v.images, 0, 'Vehicul')}
                        />
                      </span>
                    ) : null}
                    */}
                  </div>
                  {/* Vehicle-level actions */}
                  <div className="flex items-center gap-4 sm:self-auto self-stretch w-full sm:w-auto">
                    <Button variant="primary" size="md" className="w-full sm:w-auto text-base h-14 sm:h-auto" onClick={() => triggerVehicleUpload(vid)}>
                      <CameraIcon size={24} weight="regular" className="mr-1.5" />
                      {getVehicleUploadLabel(vid)}
                    </Button>
                    <input
                      ref={(el) => { vehicleFileInputsRef.current[vid] = el; }}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.heif"
                      multiple
                      className="sr-only absolute -m-px h-px w-px overflow-hidden p-0 whitespace-nowrap border-0"
                      aria-hidden="true"
                      tabIndex={-1}
                      onChange={(e) => handleVehicleFilesSelected(vid, e)}
                    />
                  </div>
                </CardTitle>
                  </CardHeader>
                  {!isVehicleSaleGroup && (
                  <CardContent className="pt-6">
                    {/* Vehicle details below header are commented out for stripped down version
                    {v && (
                      <div className="text-sm space-y-1.5 mb-3">
                        {!!(v.vin_number) && (
                          <div className="flex justify-between"><span className="text-gray-600">VIN:</span><span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{v.vin_number}</span></div>
                        )}
                        {!!(v.body_type) && (
                          <div className="flex justify-between"><span className="text-gray-600">Caroserie:</span><span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{v.body_type}</span></div>
                        )}
                        {!!(v.build_year) && (
                          <div className="flex justify-between"><span className="text-gray-600">An fabricație:</span><span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{v.build_year}</span></div>
                        )}
                        {!!(v.engine_name) && (
                          <div className="flex justify-between"><span className="text-gray-600">Motor:</span><span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{v.engine_name}</span></div>
                        )}
                        {!!(v.engine_code_name && String(v.engine_code_name).trim().length > 0) && (
                          <div className="flex justify-between"><span className="text-gray-600">Cod motor:</span><span className="inline-flex items-center px-0.5 py-0.5 text-sm sm:text-xs text-gray-700 font-semibold">{v.engine_code_name}</span></div>
                        )}
                      </div>
                    )}
                    */}

                    {/* Vehicle images now shown inline in header */}

                    {/* Parts for this vehicle - subtle card rows instead of separators */}
                    <div className="space-y-4">
                      {items.map((li) => (
                        <div key={li.id}>
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0 flex-1 flex items-center gap-4">
                              <span className="text-base sm:text-lg font-semibold leading-snug whitespace-normal break-words sm:truncate">
                                {li.component_name || li.component_id || (String(li?.line_item_type || '') === 'vehicle_sale' ? 'vehicul pentru dezmembrare' : 'piesa nespecificată')}
                                {Number(li.quantity ?? 1) !== 1 ? (
                                  <span className="ml-2 text-sm text-gray-500 font-normal">{`${Number(li.quantity ?? 1)} buc`}</span>
                                ) : null}
                              </span>
                              {/* Part images disabled for now
                              {!!(li.images?.length) && (
                                <span className="inline-flex items-center">
                                  <LineItemImageThumb
                                    images={li.images}
                                    type="customer"
                                    disableZoom
                                    size="md"
                                    onClick={() => openGallery(li.images, 0, li.component_name || 'Articol')}
                                  />
                                </span>
                              )}
                              */}
                            </div>
                            <div className="flex-shrink-0">
                              {(() => {
                                const filesCount = Number(lineItemFileCounts[String(li.id)] || 0);
                                const commentsCount = ((commentDrafts[String(li.id)] || '').trim().length > 0) ? 1 : 0;
                                const totalCount = filesCount + commentsCount;
                                return (
                                  <Button
                                    size="md"
                                    variant="secondary"
                                    className="text-base"
                                    onClick={() => openLineItemDialog(li.id)}
                                  >
                                    {totalCount > 0 ? (
                                      <span className="font-medium">({totalCount})</span>
                                    ) : null}
                                    Adaugă
                                  </Button>
                                );
                              })()}
                            </div>
                          </div>
                          {/* Expanded section removed; editing is handled via dialog */}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                  )}
                </Card>
              );
            })}
          </section>
        );
      })()}
      {/** Image gallery overlay removed for stripped down version */}

      {/* Line Item Edit Dialog */}
      <Dialog open={lineItemDialogOpen} onOpenChange={setLineItemDialogOpen}>
        <DialogContent hideClose preventClose onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>
              {(() => {
                if (!activeLineItemId) return 'Articol';
                const li = (data?.line_items || []).find(x => String(x.id) === String(activeLineItemId));
                const name = li ? (li.component_name || li.component_id || 'Articol') : 'Articol';
                const qty = li ? Number(li.quantity ?? 1) : 1;
                return (
                  <span className="text-base sm:text-lg font-semibold leading-snug whitespace-normal break-words sm:truncate">
                    {name}
                    {qty !== 1 ? (
                      <span className="ml-2 text-sm text-gray-500 font-normal">{`${qty} buc`}</span>
                    ) : null}
                  </span>
                );
              })()}
            </DialogTitle>

          </DialogHeader>
          <div className="space-y-2">
            <TextArea
              ref={lineItemTextAreaRef}
              className="w-full"
              inputClassName="text-base placeholder:text-base"
              placeholder="Note piesa: ex: coduri OEM, culoare, etc"
              value={lineItemDialogComment}
              onChange={setLineItemDialogComment}
              rows={3}
            />
            <div className="w-full">
              <Button
                variant="primary"
                size="md"
                className="w-full text-base h-14 sm:h-auto"
                onClick={() => activeLineItemId && triggerLineItemUpload(activeLineItemId)}
                disabled={!activeLineItemId}
              >
                <CameraIcon size={24} weight="regular" className="mr-1.5" />
                {activeLineItemId ? getLineItemUploadLabel(activeLineItemId) : 'Imagini'}
              </Button>
              {/* Hidden input scoped to active line item */}
              {activeLineItemId && (
                <input
                  ref={(el) => { if (activeLineItemId) lineItemFileInputsRef.current[String(activeLineItemId)] = el; }}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.heif"
                  multiple
                  className="sr-only absolute -m-px h-px w-px overflow-hidden p-0 whitespace-nowrap border-0"
                  aria-hidden="true"
                  tabIndex={-1}
                  onChange={(e) => handleLineItemFilesSelected(activeLineItemId, e)}
                />
              )}
            </div>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 w-full">
            <Button variant="outline" size="md" className="text-base w-full" onClick={handleLineItemDialogCancel}>
              <XIcon size={18} weight="regular" className="mr-1.5" />
              Anulează
            </Button>
            <Button variant="secondary" size="md" className="text-base w-full" onClick={handleLineItemDialogSave}>
              <CheckIcon size={18} weight="regular" className="mr-1.5" />
              Salvează
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

